import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getIcon } from "@/lib/agents";
import { CheckCircle2, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [applying, setApplying] = useState(null);

  const load = () => api.get("/templates").then((r) => setItems(r.data.templates));
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    const { data } = await api.get(`/templates/${id}`);
    setDetail(data);
  };

  const apply = async (id) => {
    setApplying(id);
    try {
      const { data } = await api.post(`/templates/${id}/apply`);
      toast.success(`Aplicado · ${data.policies_added} políticas + ${data.playbooks_added} playbooks`);
    } catch {
      toast.error("Fallo al aplicar");
    } finally {
      setApplying(null);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="templates-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">TENANT TEMPLATES · SETUP EN 60 SEGUNDOS</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Templates por Industria</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Aplica un paquete listo (políticas + playbooks + KPIs + agentes clave) para tu sector.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {items.map((t) => {
          const Icon = getIcon(t.icon);
          return (
            <div
              key={t.id}
              className="border border-[var(--border)] bg-[var(--surface)] p-5 hover-neon flex flex-col"
              data-testid={`tpl-${t.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div
                  className="w-11 h-11 border flex items-center justify-center"
                  style={{ borderColor: t.color, color: t.color }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <Badge className="rounded-none font-mono text-[10px] bg-[var(--surface-2)] text-white hover:bg-[var(--surface-2)] border border-[var(--border)]">
                  {t.policies_count + t.playbooks_count} items
                </Badge>
              </div>
              <div className="font-display font-bold text-lg mb-1">{t.label}</div>
              <p className="text-xs text-[var(--text-dim)] mb-4 leading-relaxed flex-1">{t.description}</p>
              <div className="flex gap-2 mt-auto">
                <Button
                  onClick={() => open(t.id)}
                  variant="outline"
                  className="flex-1 rounded-none border-[var(--border)] hover:border-[var(--accent)]"
                  data-testid={`tpl-view-${t.id}`}
                >
                  Ver
                </Button>
                <Button
                  onClick={() => apply(t.id)}
                  disabled={applying === t.id}
                  className="flex-1 rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80"
                  data-testid={`tpl-apply-${t.id}`}
                >
                  <Zap className="w-3 h-3 mr-1" />
                  {applying === t.id ? "…" : "Aplicar"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {detail && (
        <div className="border border-[var(--accent)] bg-[var(--surface)] p-6" data-testid="tpl-detail">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono mb-1">DETALLE · {detail.label.toUpperCase()}</div>
              <div className="font-display font-bold text-2xl">{detail.label}</div>
            </div>
            <button
              onClick={() => setDetail(null)}
              className="text-[var(--text-dim)] hover:text-white text-xs font-mono"
              data-testid="tpl-close"
            >
              CERRAR ✕
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Section title="AGENTES CLAVE">
              {detail.focus_agents?.map((a) => (
                <span key={a} className="font-mono text-[10px] text-[var(--accent)] uppercase block">
                  {a}
                </span>
              ))}
            </Section>
            <Section title="POLÍTICAS INCLUIDAS">
              {detail.policies?.map((p, i) => (
                <div key={i} className="mb-2 pb-2 border-b border-[var(--border)] last:border-0">
                  <div className="text-xs text-white">{p.title}</div>
                  <div className="text-[10px] text-[var(--text-dim)] font-mono uppercase">{p.category}</div>
                </div>
              ))}
            </Section>
            <Section title="PLAYBOOKS INCLUIDOS">
              {detail.playbooks?.map((p, i) => (
                <div key={i} className="mb-2 pb-2 border-b border-[var(--border)] last:border-0">
                  <div className="text-xs text-white">{p.title}</div>
                  <div className="text-[10px] text-[var(--text-dim)] font-mono">{p.steps.length} pasos</div>
                </div>
              ))}
            </Section>
          </div>
          {detail.kpis && (
            <div className="mt-4 pt-4 border-t border-[var(--border)]">
              <div className="label-mono mb-2">KPIs OBJETIVO</div>
              <div className="flex flex-wrap gap-2">
                {detail.kpis.map((k) => (
                  <Badge key={k} className="rounded-none bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/40 font-mono text-[10px]">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> {k}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="border border-[var(--border)] bg-[var(--bg)] p-4">
    <div className="label-mono mb-2">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);
